import React, { useState } from "react"
import Link from "components/atoms/Link/Link"
import Status from "components/atoms/Status/Status"
import { useProfileFeature } from "hooks/useProfile"
import { Trans, useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { ProfileOfferService } from "services/api-service"
import { OfferType } from "services/api-service/types"
import { SentryService } from "services/SentryService"
import { RootState } from "store/store"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Tag from "ui/components/atoms/tag"
import { H3, LabelH2 } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { formatDate } from "utils/date"

const OfferBlock = ({ data: initData }: { data: OfferType }) => {
  const navigate = useNavigate()
  const { profile } = useSelector((state: RootState) => state.profile)
  const hasOfferAnalytics = useProfileFeature("analytics")
  const { t, i18n } = useTranslation(["panel", "form", "common", "data"])

  const [data, setData] = useState<OfferType>(initData)
  const [boosts, setBoosts] = useState<null | number>(initData.boosts)
  const [loadingBoost, setLoadingBoost] = useState<boolean>(false)

  const today = new Date()
  const date = data.status === "pending" ? data.paymentExpiredDate : data.expiredDate
  const dtCreated = new Date(data.dtCreated)
  const expiredDate = new Date(date)
  const totalDays = Math.round((expiredDate.getTime() - dtCreated.getTime()) / (1000 * 3600 * 24))
  const nowDays = Math.round((expiredDate.getTime() - today.getTime()) / (1000 * 3600 * 24))

  const handleBoost = async () => {
    try {
      setLoadingBoost(true)
      const res = await ProfileOfferService.boostOffer(`${data.id}`)
      setData(res)
      setBoosts(res.boosts)
      setLoadingBoost(false)
      window.alert(t("offers.offer.boost.alert.success"))
    } catch (e) {
      SentryService.error("[ERROR ProfileOfferService.boostOffer]:", e)
      setLoadingBoost(false)
      window.alert(t("offers.offer.boost.alert.error"))
    }
  }

  return (
    <>
      <Wrapper>
        <Content>
          <Box $marginBottom="M">
            <HeaderWrapper>
              <Status label={data.status} />
              <LabelH2 $marginBottom="0">{data.name}</LabelH2>
            </HeaderWrapper>
            <TagsWrapper>
              <Tag
                $type="secondary"
                $showBorder={false}
                $background="background"
                icon="tag"
                $iconColor="primary"
                label={`${t(data.category, { ns: "data" })}, ${t(data.subcategory, {
                  ns: "data",
                })}`}
              />
              <Tag
                $type="secondary"
                $showBorder={false}
                $background="background"
                icon="map"
                $iconColor="primary"
                label={`${data.address.city}, ${data.address.countryCode.toUpperCase()}`}
              />
              {data.surveyName && (
                <Tag
                  $type="secondary"
                  $showBorder={false}
                  $background="background"
                  icon="check-square"
                  $iconColor="primary"
                  label={data.surveyName}
                />
              )}
              <Tag
                $type="secondary"
                $showBorder={false}
                $background="background"
                icon="calendar"
                $iconColor="primary"
                label={`${formatDate(data.dtCreated, i18n.language, "do MMMM yyyy")}${
                  data.expiredDate
                    ? `-> ${formatDate(data.expiredDate, i18n.language, "do MMMM yyyy")}`
                    : ""
                }`}
              />
              {data.lastBoost && (
                <Tag
                  $type="secondary"
                  $showBorder={false}
                  $background="background"
                  icon="boost"
                  $iconColor="primary"
                  label={`${formatDate(data.lastBoost, i18n.language, "do MMMM yyyy")}`}
                />
              )}
            </TagsWrapper>
          </Box>
          {/*{(data.status === "active" || data.status === "pending") && nowDays >= 0 && (*/}
          {/*  <TimelineWrapper>*/}
          {/*    <Timeline*/}
          {/*      precent={((totalDays - nowDays) / totalDays) * 100}*/}
          {/*      color={nowDays > 2 ? "primary" : nowDays === 0 ? "red" : "orange"}*/}
          {/*    >*/}
          {/*      /!*<p>{diffrenceInDays}</p>*!/*/}
          {/*    </Timeline>*/}
          {/*    {nowDays === 0 && <Label type="third">Oferta dziś wygaśnie</Label>}*/}
          {/*    {nowDays <= 2 && nowDays > 0 && (*/}
          {/*      <Label type="third">Oferta wygaśnie za {nowDays} dni</Label>*/}
          {/*    )}*/}
          {/*    {nowDays > 2 && <Label type="third">Pozostało {nowDays} dni</Label>}*/}
          {/*  </TimelineWrapper>*/}
          {/*)}*/}
        </Content>
        {data.status === "pending" && (
          <>
            <Alert $type="warning" $marginBottom="S" $small>
              <Trans
                i18nKey={t("offers.offer.pending-info")}
                components={{
                  href: <a target="_blank" rel="noreferrer" href="mailto:help@jobintrans.eu" />,
                }}
              />
            </Alert>
            <ButtonsWrapper status={data.status}>
              {/*<Button*/}
              {/*  $type="secondary"*/}
              {/*  icon="edit"*/}
              {/*  onClick={() => navigate(`/edit-offer/${data.id}`)}*/}
              {/*>*/}
              {/*  {t("offers.offer.buttons.edit")}*/}
              {/*</Button>*/}
              {!!data.transaction && (
                <Button
                  $type="secondary"
                  icon="clock"
                  onClick={() => navigate(`/transaction/status/${data.id}/${data.transaction?.id}`)}
                >
                  {t("offers.offer.buttons.status")}
                </Button>
              )}
            </ButtonsWrapper>
          </>
        )}
        {data.status === "limited" && (
          <Alert $type="warning" $marginBottom="S" $small>
            <Trans
              i18nKey={t("offers.offer.limited-info")}
              components={{
                href: <a target="_blank" rel="noreferrer" href="mailto:help@jobintrans.eu" />,
              }}
            />
          </Alert>
        )}
        <ButtonsWrapper status={data.status}>
          {(data.status === "active" || data.status === "hidden") && (
            <>
              <a
                href={`${process.env.REACT_APP_DOMAIN}/pl/offers/${data.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button $type="secondary" icon="eye">
                  {t("offers.offer.buttons.see")}
                </Button>
              </a>
              {/*<Button*/}
              {/*  $type="secondary"*/}
              {/*  icon="edit"*/}
              {/*  onClick={() => navigate(`/edit-offer/${data.id}`)}*/}
              {/*>*/}
              {/*  {t("offers.offer.buttons.edit")}*/}
              {/*</Button>*/}
              {hasOfferAnalytics && (
                <Button
                  $type="secondary"
                  icon="target"
                  onClick={() => navigate(`m/${data.id}/analytics`)}
                >
                  {t("offers.offer.buttons.statistics")}
                </Button>
              )}
              {data.plan !== "draft" && !!boosts && boosts > 0 && (
                <Button $type="secondary" icon="boost" onClick={handleBoost} loading={loadingBoost}>
                  {`${t("offers.offer.buttons.boost")} (${boosts})`}
                </Button>
              )}
              {data.plan === "draft" && (
                <Link $type="secondary" icon="clock" to={`/extend-offer/${data.id}`}>
                  {t("offers.offer.buttons.extend")}
                </Link>
              )}
              <Button
                $type="primary"
                icon="check-square"
                onClick={() => navigate(`/panel/offers/${data.id}/applications`)}
              >
                {t("offers.offer.buttons.candidates")}
              </Button>
            </>
          )}
          {data.status === "deactivated" && (
            <>
              <Button
                $type="primary"
                icon="check-square"
                onClick={() => navigate(`/panel/offers/${data.id}/applications`)}
              >
                {t("offers.offer.buttons.candidates")}
              </Button>
            </>
          )}
          {data.status === "limited" && (
            <>
              <a
                href={`${process.env.REACT_APP_DOMAIN}/pl/offers/${data.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button $type="secondary" icon="eye">
                  {t("offers.offer.buttons.see")}
                </Button>
              </a>
              {/*<Button*/}
              {/*  $type="secondary"*/}
              {/*  icon="edit"*/}
              {/*  onClick={() => navigate(`/edit-offer/${data.id}`)}*/}
              {/*>*/}
              {/*  {t("offers.offer.buttons.edit")}*/}
              {/*</Button>*/}
              {hasOfferAnalytics && (
                <Button
                  $type="secondary"
                  icon="target"
                  onClick={() => navigate(`m/${data.id}/analytics`)}
                >
                  {t("offers.offer.buttons.statistics")}
                </Button>
              )}
              <Link $type="secondary" icon="clock" to={`/extend-offer/${data.id}`}>
                {t("offers.offer.buttons.extend")}
              </Link>
              <Button
                $type="primary"
                icon="check-square"
                onClick={() => navigate(`/panel/offers/${data.id}/applications`)}
              >
                {t("offers.offer.buttons.candidates")}
              </Button>
            </>
          )}
        </ButtonsWrapper>
      </Wrapper>
    </>
  )
}

export default OfferBlock

const ButtonsWrapper = styled(Box)<{
  status: string
}>`
  display: flex;
  justify-content: flex-end;
  grid-gap: ${({ theme }) => theme.space.XS};

  @media (max-width: 1200px) {
    display: grid;
    grid-template-columns: ${({ status }) => (status === "active" ? "1fr 1fr" : "1fr")};

    button {
      width: 100%;
    }
  }
`

const TagsWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${({ theme }) => theme.space.XS};
  margin-bottom: ${({ theme }) => theme.space.XXS};
`

const HeaderWrapper = styled(Box)`
  display: flex;
  align-items: flex-end;
  grid-gap: ${({ theme }) => theme.space.XS};
  margin-bottom: ${({ theme }) => theme.space.XS};
`

const Content = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.space.S};

  @media (max-width: 900px) {
    display: grid;
    grid-template-columns: 1fr 1fr;

    button {
      width: 100%;
    }
  }
`

const Wrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  border: 1px solid ${({ theme }) => theme.color.border};
  background: white;
`
